import { useEffect, useState } from 'react';

import {
  listProperties,
  PropertyWalletItemDTO,
  PropertyWalletEstateContextDTO
} from '../../../services/properties';
import { FiltersState } from '../types';

type Params = {
  initialFilters: FiltersState;
  enabled: boolean;
};

export const usePropertiesWalletList = ({ initialFilters, enabled }: Params) => {
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<PropertyWalletItemDTO[]>([]);
  const [estateContext, setEstateContext] = useState<PropertyWalletEstateContextDTO | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<FiltersState>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<FiltersState>(initialFilters);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isScopeError, setIsScopeError] = useState(false);

  const listEnabled = enabled || Boolean(appliedFilters.estateId);

  const loadData = async (page = currentPage, pageFilters = appliedFilters) => {
    setIsLoading(true);
    setErrorMessage(null);
    setIsScopeError(false);

    try {
      const response = await listProperties({
        page,
        limit: 10,
        estateId: pageFilters.estateId || undefined,
        search: pageFilters.search || undefined,
        type: pageFilters.type || undefined,
        orderBy: pageFilters.orderBy,
        orderByDir: pageFilters.orderByDir
      });

      setItems(response.items || []);
      setTotal(response.resultsInfo?.total || 0);
      setEstateContext(response.estate || null);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Não foi possível carregar a carteira de imóveis no momento. Tente novamente em alguns instantes.';

      const scopeError =
        message.includes('vinculada à base de imóveis') ||
        message.includes('estateId é obrigatório');

      setIsScopeError(scopeError);
      setErrorMessage(message);
      setItems([]);
      setTotal(0);
      setEstateContext(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!listEnabled) return;
    loadData(currentPage, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters, listEnabled]);

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
    setAppliedFilters(initialFilters);
  };

  return {
    isLoading,
    items,
    total,
    estateContext,
    currentPage,
    setCurrentPage,
    filters,
    setFilters,
    appliedFilters,
    listEnabled,
    errorMessage,
    isScopeError,
    loadData,
    handleSearch,
    clearFilters
  };
};
